import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../LanguageContext";
import { optImg } from "../lib/img";

const ProductModal = ({ openId, onClose }) => {
  const { t, lang, collections } = useLang();
  const navigate = useNavigate();
  const COLLECTIONS = collections;
  const item = COLLECTIONS.find((c) => c.id === openId);

  useEffect(() => {
    document.body.style.overflow = openId ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [openId]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div className="fixed inset-0 z-[60] flex" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
            className="relative ml-auto h-full w-full max-w-2xl bg-paper overflow-y-auto"
          >
            <div className="relative h-72 md:h-96 overflow-hidden">
              <img src={optImg(item.image, 1000)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-paper via-transparent to-ink/20" />
              <button onClick={onClose} className="absolute top-6 right-6 h-11 w-11 flex items-center justify-center bg-ink/50 text-white hover:bg-ink transition-colors rounded-full">
                <X size={20} strokeWidth={1.3} />
              </button>
              <span className="absolute bottom-5 left-8 font-serif text-white text-lg">{item.number}</span>
            </div>

            <div className="px-8 md:px-12 py-10">
              <p className="overline text-gold">{item[lang].short}</p>
              <h3 className="font-serif text-ink text-4xl md:text-5xl font-light mt-4">{item[lang].name}</h3>
              <p className="mt-6 text-ink/65 text-lg font-light leading-relaxed">{item[lang].desc}</p>

              <ul className="mt-10 space-y-4 border-t border-border pt-8">
                {item[lang].points.map((p, i) => (
                  <li key={i} className="flex items-center gap-4 text-ink">
                    <span className="h-8 w-8 flex items-center justify-center border border-gold text-gold rounded-full shrink-0">
                      <Check size={15} strokeWidth={1.5} />
                    </span>
                    <span className="font-light text-lg">{p}</span>
                  </li>
                ))}
              </ul>

              {item.gallery && item.gallery.length > 0 && (
                <div className="mt-10 grid grid-cols-3 gap-3">
                  {item.gallery.slice(0, 3).map((g, i) => (
                    <button key={i} onClick={() => navigate(`/produkt/${item.id}`)}
                      className="relative aspect-square overflow-hidden group">
                      <img src={optImg(g.image, 400)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/20 transition-colors" />
                    </button>
                  ))}
                </div>
              )}

              <button onClick={() => navigate(`/produkt/${item.id}`)}
                className="group mt-8 w-full flex items-center justify-between gap-3 bg-ink hover:bg-ink-soft text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors">
                {lang === "sv" ? "Se hela produktsidan" : "View full product page"}
                <ArrowUpRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>

              <button onClick={() => { onClose(); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 300); }}
                className="group mt-3 w-full flex items-center justify-center gap-3 border border-ink/25 hover:border-gold text-ink px-7 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors">
                {t.cta.contactUs}
                <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;
